import type { ReactNode } from "react";
import { motion, useMotionValue, useTransform, type PanInfo } from "framer-motion";
import type { Categorization } from "../types/transaction";
import { useReceiptUpload } from "../hooks/useReceiptUpload";
import { accountLabel } from "../utils/accounts";
import { formatAmount, isNegativeAmount } from "../utils/currency";
import { formatShortDate } from "../utils/dates";
import { CardActions } from "./review/CardActions";
import { ReceiptButton, ReceiptStatus } from "./review/CardReceipt";

interface TransactionCardProps {
  item: Categorization;
  clientId: number;
  /**
   * Account the user picked in the selector but has not yet finalized.
   * When set (and different from item.account_name), the card shows this
   * as a pending change instead of the AI suggestion. null = no pending change.
   */
  previewAccount?: string | null;
  onConfirm: () => void;
  onShowAlternatives: () => void;
  onSplit?: () => void;
  isTop: boolean;
  index: number;
  /** The server's label for an account it names (the pick list's `labels`). */
  labels?: Record<string, string>;
  /** True after the server refused this row because its account keeps its own books. */
  held?: boolean;
  /** The row's own question or step (the transfer question, Redo), under the reasoning. */
  extra?: ReactNode;
}

// The server's needs-attention sentence, word for word
// (backend/prime/ledger/exceptions.py NEEDS_ATTENTION_MESSAGE; requirements C18).
const NEEDS_ATTENTION_MESSAGE =
  "Your bank changed this entry, so it stays as it is here. Your bank's " +
  "correction is listed as its own entry. You can reach us at support@eledger.com.";

const SWIPE_THRESHOLD = 100;
const ROTATION_FACTOR = 0.1;

// A card only ever leaves the deck by being approved to the ledger (confirm or
// corrected-confirm) — which in the swipe language is a rightward motion.
// Swipe-left opens the account selector WITHOUT removing the card, so there is
// no leftward exit. Reading the drag position here was the C4 bug: the Confirm
// button never moves x, so x.get() was 0 and the card flew left (the "reject"
// direction) while actually confirming. A constant rightward exit matches swipe
// semantics for both the button and swipe-right.
export const CARD_EXIT_ANIMATION = {
  opacity: 0,
  x: 300,
  rotate: 15,
  transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] as const },
};

export function TransactionCard({
  item,
  clientId,
  previewAccount = null,
  onConfirm,
  onShowAlternatives,
  onSplit,
  isTop,
  index,
  labels = {},
  held = false,
  extra = null,
}: TransactionCardProps): ReactNode {
  const receipt = useReceiptUpload(clientId);
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-ROTATION_FACTOR * 20, ROTATION_FACTOR * 20]);
  const confirmOpacity = useTransform(x, [0, SWIPE_THRESHOLD], [0, 1]);
  const correctOpacity = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0]);
  const bgColor = useTransform(
    x,
    [-SWIPE_THRESHOLD, 0, SWIPE_THRESHOLD],
    ["rgba(234, 179, 8, 0.12)", "rgba(255, 255, 255, 0)", "rgba(22, 163, 74, 0.12)"],
  );

  const scale = isTop ? 1 : 0.95;
  const yOffset = isTop ? 0 : 6;
  const zIndex = 10 - index;

  // A needs-attention card shows the server's sentence in place of every
  // control: no Confirm, no swipe (D1 amendment 2.8 (8), C18).
  const isActionable = item.status !== "uncategorized" && !item.needs_attention;

  function handleDragEnd(_: unknown, info: PanInfo): void {
    if (!isActionable) return;
    if (info.offset.x > SWIPE_THRESHOLD) {
      hapticTap();
      onConfirm();
    } else if (info.offset.x < -SWIPE_THRESHOLD) {
      hapticTap();
      onShowAlternatives();
    }
  }

  const amountClass = isNegativeAmount(item.amount)
    ? "card-amount negative"
    : "card-amount positive";

  // A previewed account (chosen in the selector but not yet finalized) is shown
  // in place of the AI suggestion so the user can confirm or reject the change.
  const isPreviewingChange = previewAccount != null && previewAccount !== item.account_name;
  const displayedAccount = isPreviewingChange ? previewAccount : item.account_name;

  return (
    <motion.div
      className={held ? "transaction-card held" : "transaction-card"}
      data-held={held ? "true" : undefined}
      style={{
        x,
        rotate,
        backgroundColor: bgColor,
        zIndex,
        scale,
        y: yOffset,
      }}
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={isTop ? handleDragEnd : undefined}
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{
        opacity: 1,
        scale,
        y: yOffset,
      }}
      data-top={isTop ? "true" : "false"}
      exit={CARD_EXIT_ANIMATION}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      whileHover={isTop ? { y: -2 } : undefined}
    >
      {/* Swipe indicators — hidden for uncategorized items */}
      {isActionable && (
        <>
          <motion.div className="swipe-indicator swipe-confirm" style={{ opacity: confirmOpacity }}>
            <span className="swipe-icon">&#10003;</span>
            <span>Confirm</span>
          </motion.div>
          <motion.div className="swipe-indicator swipe-correct" style={{ opacity: correctOpacity }}>
            <span className="swipe-icon">&#8230;</span>
            <span>Alternatives</span>
          </motion.div>
        </>
      )}

      {/* Card content */}
      <div className="card-top-row">
        <span className="card-date">{formatShortDate(item.date)}</span>
        <span className={amountClass}>{formatAmount(item.amount)}</span>
      </div>

      <div className="card-merchant">{item.description || "Unknown"}</div>

      {item.status === "uncategorized" ? (
        <div className="card-suggestion">
          <span className="card-suggestion-label">Awaiting categorization</span>
        </div>
      ) : (
        <div className="card-suggestion">
          <span className="card-suggestion-label">
            {isPreviewingChange ? "Changed to:" : "AI suggests:"}
          </span>
          <span className="card-suggestion-account">{accountLabel(displayedAccount, labels)}</span>
        </div>
      )}

      {item.confidence_tier && (
        <div className="card-confidence">
          {item.confidence_tier.charAt(0) + item.confidence_tier.slice(1).toLowerCase()} confidence
        </div>
      )}

      {item.reasoning && <div className="card-reasoning">{item.reasoning}</div>}

      {extra}

      <ReceiptStatus receipt={receipt} clientId={clientId} />

      {item.needs_attention && <div className="card-reasoning">{NEEDS_ATTENTION_MESSAGE}</div>}

      {/* Action buttons: categorized items get full actions */}
      {isTop && isActionable && (
        <CardActions
          canSplit={item.can_split}
          onConfirm={() => {
            hapticTap();
            onConfirm();
          }}
          onShowAlternatives={onShowAlternatives}
          onSplit={onSplit}
        >
          <ReceiptButton receipt={receipt} />
        </CardActions>
      )}

      {/* Uncategorized items: receipt button only */}
      {isTop && item.status === "uncategorized" && (
        <div className="card-actions">
          <ReceiptButton receipt={receipt} />
        </div>
      )}
    </motion.div>
  );
}

function hapticTap(): void {
  if ("vibrate" in navigator) {
    navigator.vibrate(10);
  }
}
